import React, { useEffect, useState } from "react";
import { Lock, FileText, ChevronDown, Loader2, Calendar, ShieldCheck } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { fetchNdaAcceptances } from "../../lib/ndaApi";
import { useUserPref } from "../../lib/useUserPref";
import NdaDocumentDialog from "./NdaDocumentDialog";

function formatIt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("it-IT", {
      day: "2-digit",
      month: "long",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

/**
 * "Documenti Legali" section for the personal area — displays the user's
 * NDA acceptance history (chronological descending). Each entry shows the
 * exact version, timestamp, and a "Visualizza accordo" link that reopens
 * the immutable document text for that specific version.
 */
export default function NdaAcceptancesSection() {
  const { ndaState } = useAuth();
  const isFounder = !!(ndaState && ndaState.is_founder);
  // Persistenza per-utente (cloud + localStorage) dello stato aperto/chiuso.
  // Default: aperto (retro-compatibile con il comportamento precedente).
  const [open, setOpen] = useUserPref(
    "personalArea:legalDocumentsOpen",
    true
  );
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [docVersion, setDocVersion] = useState(null);

  useEffect(() => {
    // Founder is not a receiving party — no acceptance list to fetch.
    if (isFounder) {
      setItems([]);
      return () => {};
    }
    let cancelled = false;
    fetchNdaAcceptances()
      .then((data) => {
        if (!cancelled) setItems(Array.isArray(data?.items) ? data.items : []);
      })
      .catch((e) => {
        if (!cancelled)
          setError(e?.response?.data?.detail || "Impossibile caricare i documenti.");
      });
    return () => {
      cancelled = true;
    };
  }, [isFounder]);

  return (
    <>
      <div
        className="mt-8 glass-card p-6"
        data-testid="legal-documents-card"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3 min-w-0">
            <div
              data-testid="legal-documents-icon"
              className="w-10 h-10 rounded-xl bg-black/40 border border-orange-400/60 flex items-center justify-center flex-shrink-0"
            >
              <Lock className="w-5 h-5 text-red-400" />
            </div>
            <div className="min-w-0">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                data-testid="legal-documents-toggle"
                aria-expanded={open}
                aria-controls="legal-documents-body"
                className="flex items-center gap-1.5 text-[10px] tracking-[0.28em] uppercase text-white/40 hover:text-white/60 transition-colors"
              >
                Documenti Legali
                <ChevronDown
                  className={`w-3 h-3 text-white/40 transition-transform duration-300 motion-reduce:transition-none ${
                    open ? "rotate-0" : "-rotate-90"
                  }`}
                  aria-hidden="true"
                />
              </button>
              <div className="mt-1 text-sm text-white/85 font-light">
                Accordi accettati elettronicamente
              </div>
            </div>
          </div>
        </div>

        <div
          id="legal-documents-body"
          className={`grid transition-all duration-300 motion-reduce:transition-none ${
            open ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
          }`}
        >
          <div className="overflow-hidden">
            {items === null && !error && (
              <div
                data-testid="legal-documents-loading"
                className="py-4 flex items-center gap-2 text-[12px] text-white/40"
              >
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                Caricamento…
              </div>
            )}

            {error && (
              <div
                data-testid="legal-documents-error"
                className="text-[12px] text-red-300 bg-red-500/[0.08] border border-red-500/20 rounded-lg px-4 py-3"
              >
                {typeof error === "string" ? error : "Errore nel caricamento."}
              </div>
            )}

            {items && items.length === 0 && !error && !isFounder && (
              <div
                data-testid="legal-documents-empty"
                className="text-sm text-white/45 font-light leading-relaxed"
              >
                Non hai ancora accettato nessun documento legale.
              </div>
            )}

            {isFounder && !error && (
              <div
                data-testid="legal-documents-founder"
                className="rounded-xl border border-emerald-400/20 bg-emerald-400/[0.04] px-4 py-4 flex items-start gap-3"
              >
                <ShieldCheck className="w-4 h-4 text-emerald-300 mt-0.5 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-[14px] text-white/85 font-light">
                    Accordo di Riservatezza
                  </div>
                  <div className="mt-0.5 text-[11px] tracking-[0.18em] uppercase text-emerald-300/85">
                    NDA non richiesto per questo account
                  </div>
                </div>
              </div>
            )}

            {items && items.length > 0 && (
              <ul
                data-testid="legal-documents-list"
                className="space-y-3"
              >
                {items.map((it) => (
                  <li
                    key={it.id}
                    data-testid={`nda-acceptance-${it.version}`}
                    className="rounded-xl border border-white/8 bg-white/[0.02] px-4 py-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Lock className="w-3.5 h-3.5 text-emerald-300/85" />
                        <span className="text-[14px] text-white/85 font-light">
                          Accordo di Riservatezza — NDA v{it.version}
                        </span>
                      </div>
                      <div className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-white/45">
                        <Calendar className="w-3 h-3" />
                        Accettato il {formatIt(it.accepted_at)}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setDocVersion(it.version)}
                      data-testid={`nda-view-${it.version}`}
                      className="inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/15 text-[10px] tracking-[0.24em] uppercase text-white/75 hover:text-white hover:border-white/30 hover:bg-white/[0.04] transition-all flex-shrink-0"
                    >
                      <FileText className="w-3.5 h-3.5" />
                      Visualizza accordo
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <NdaDocumentDialog
        open={!!docVersion}
        version={docVersion}
        onClose={() => setDocVersion(null)}
      />
    </>
  );
}
